import { Box, Grid, Typography, makeStyles, Theme, Tooltip } from '@material-ui/core';
import { Error } from '@material-ui/icons';

export interface OwnershipReconciliationTableProps {
  data: Record<string, any>;
}

interface OwnerRow {
  borrowerName?: string;
  ownershipPercentage?: string | number;
  title?: string;
  matched?: string | boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(2),
  },
  panel: {
    border: '1px solid #E0E0E0',
    borderRadius: 8,
    overflow: 'hidden',
    height: '100%',
  },
  panelHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FAFAFA',
    borderBottom: '1px solid #E0E0E0',
    padding: theme.spacing(1.5, 2),
  },
  panelTitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontWeight: 'bold',
    letterSpacing: '0.05em',
  },
  count: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    borderBottom: '1px solid #E0E0E0',
    padding: theme.spacing(1.5, 2),
    '&:last-child': { borderBottom: 'none' },
  },
  nameGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  flagIcon: {
    fontSize: '1.125rem',
    flexShrink: 0,
  },
  name: {
    fontSize: '0.875rem',
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
  title: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
  },
  percentage: {
    fontSize: '0.875rem',
    fontWeight: 'bold',
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  },
  empty: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    padding: theme.spacing(1.5, 2),
  },
}));

const BORROWERS = 'borrowers';
const ENTITY_NAME = 'borrowerEntityName';

const toOwners = (value: any): OwnerRow[] => (Array.isArray(value) ? value : []);

const formatPercentage = (value: OwnerRow['ownershipPercentage']) => {
  if (value === null || value === undefined || value === '') return '';
  return `${value}%`;
};

const OwnerPanel = ({ title, owners }: { title: string; owners: OwnerRow[] }) => {
  const classes = useStyles();
  return (
    <Box className={classes.panel}>
      <Box className={classes.panelHead}>
        <Typography className={classes.panelTitle}>{title}</Typography>
        <Typography className={classes.count}>
          {owners.length} {owners.length === 1 ? 'owner' : 'owners'}
        </Typography>
      </Box>
      {owners.length === 0 ? (
        <Typography className={classes.empty}>No owners</Typography>
      ) : (
        owners.map((owner, index) => {
          const isUnmatched = String(owner.matched) === 'false';
          return (
            <Box className={classes.row} key={`${owner.borrowerName ?? ''}-${index}`}>
              <Box className={classes.nameGroup}>
                {isUnmatched && (
                  <Tooltip title="This borrower has no matching counterpart.">
                    <Error className={classes.flagIcon} color="error" />
                  </Tooltip>
                )}
                <Box minWidth={0}>
                  <Typography className={classes.name}>{owner.borrowerName}</Typography>
                  {owner.title && (
                    <Typography className={classes.title}>{owner.title}</Typography>
                  )}
                </Box>
              </Box>
              <Typography className={classes.percentage}>
                {formatPercentage(owner.ownershipPercentage)}
              </Typography>
            </Box>
          );
        })
      )}
    </Box>
  );
};

export const OwnershipReconciliationTable = ({ data }: OwnershipReconciliationTableProps) => {
  const classes = useStyles();
  const items = data?.items ?? [];
  const borrowers = items.find(item => item.itemName === BORROWERS);
  const entityName = items.find(item => item.itemName === ENTITY_NAME);
  if (!borrowers) {
    return null;
  }
  const subtitle = [
    entityName?.expectedData,
    'Portal ownership compared against the uploaded operating agreement',
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <>
      <Typography className={classes.subtitle}>{subtitle}</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <OwnerPanel title="PORTAL" owners={toOwners(borrowers.expectedData)} />
        </Grid>
        <Grid item xs={12} md={6}>
          <OwnerPanel title="DOCUMENT" owners={toOwners(borrowers.data)} />
        </Grid>
      </Grid>
    </>
  );
};
